/**
 * TenantsPage — aba Empresas (Sprint 6.5, Multi-tenant).
 *
 * Painel da plataforma SaaS: lista os tenants (ISPs), mostra
 * cards-resumo e permite criar/editar empresa (white-label) e
 * gerir os usuários de cada tenant.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addTenantUser,
  createTenant,
  listTenants,
  listTenantUsers,
  updateTenant,
  updateUserRole,
} from "../lib/api.js";
import DeviceMapPanel from "../components/tenants/DeviceMapPanel.jsx";
import TenantDevicesTab from "../components/tenants/TenantDevicesTab.jsx";
import TenantInventoryTab from "../components/tenants/TenantInventoryTab.jsx";
import TenantInstallationsTab from "../components/tenants/TenantInstallationsTab.jsx";

const ROLES = [
  "super_admin", "tenant_admin", "noc", "field", "viewer",
];

// SAAS-COMPANIES-UX-AUDIT-1 (quick win §9.1) — status real do tenant
// (`t.status`, já no payload). A lista antiga usava `t.active` (booleano
// legado), divergente da tela de Aprovação — unificado aqui em `status`.
const STATUS_META = {
  active:    { label: "Ativa",     cls: "border-noc-ok/40 text-noc-ok bg-noc-ok/10" },
  suspended: { label: "Suspensa",  cls: "border-noc-warn/40 text-noc-warn bg-noc-warn/10" },
  pending:   { label: "Pendente",  cls: "border-noc-accent/40 text-noc-accent bg-noc-accent/10" },
  rejected:  { label: "Rejeitada", cls: "border-noc-err/40 text-noc-err bg-noc-err/10" },
};

function tenantStatus(t) {
  // Fallback defensivo: payloads antigos sem `status` caem em active/inactive
  // conforme o booleano legado, sem quebrar a UI.
  return t.status || (t.active === false ? "suspended" : "active");
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || {
    label: status || "—", cls: "border-noc-border text-noc-muted",
  };
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5
                      rounded border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

const STATUS_FILTERS = [
  ["active", "Ativas"],
  ["suspended", "Suspensas"],
  ["pending", "Pendentes"],
  ["all", "Todas"],
];

export default function TenantsPage({ onNavigate } = {}) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // quick win §9.1/§9.4 — filtro por status (default Ativas, esconde o lixo
  // de teste sem apagar nada) + busca local por nome/slug/domínio.
  const [statusFilter, setStatusFilter] = useState("active");
  const [query, setQuery] = useState("");

  const load = useCallback(async (signal) => {
    setLoading(true);
    setErr(null);
    try {
      const data = await listTenants({ signal });
      setTenants(data.tenants || []);
    } catch (e) {
      if (e?.name !== "AbortError") {
        setErr(e?.message || "Falha ao carregar empresas.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load, reloadKey]);

  function reload() {
    setReloadKey((k) => k + 1);
  }

  // quick win §9.1/§9.4 — lista visível = filtro de status ∩ busca. Puramente
  // client-side (nenhuma request nova); default esconde os tenants de teste.
  // Declarado ANTES de qualquer return (hook incondicional).
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((t) => {
      if (statusFilter !== "all" && tenantStatus(t) !== statusFilter) return false;
      if (!q) return true;
      return [t.name, t.slug, t.domain]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [tenants, statusFilter, query]);

  if (detailId) {
    return (
      <TenantDetail
        tenantId={detailId}
        tenants={tenants}
        onBack={() => { setDetailId(null); reload(); }}
        onNavigate={onNavigate}
      />
    );
  }

  const totalDevices = tenants.reduce(
    (s, t) => s + (t.device_count || 0), 0
  );
  const totalUsers = tenants.reduce(
    (s, t) => s + (t.user_count || 0), 0
  );
  // quick win §9.2 — contagens por STATUS real (não pelo booleano legado).
  const activeCount = tenants.filter((t) => tenantStatus(t) === "active").length;
  const suspendedCount = tenants.filter((t) => tenantStatus(t) === "suspended").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Empresas</h1>
          <p className="text-xs text-noc-muted">
            Plataforma multi-empresa — tenants, white-label e
            permissões.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="h-9 px-3 rounded-lg text-sm font-semibold
                     bg-noc-accent/15 text-noc-accent border
                     border-noc-accent/40"
        >
          + Nova empresa
        </button>
      </div>

      {err && (
        <div className="text-sm text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded-lg p-3">
          {err}
        </div>
      )}

      {/* cards — quick win §9.2: Ativas/Suspensas por status real */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Empresas" value={tenants.length} />
        <StatCard label="Ativas" value={activeCount} />
        <StatCard label="Suspensas" value={suspendedCount} />
        <StatCard label="Dispositivos" value={totalDevices} />
        <StatCard label="Usuários" value={totalUsers} />
      </div>

      {/* toolbar — quick win §9.1/§9.4: filtro de status + busca local */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex rounded-lg border border-noc-border overflow-hidden
                        text-xs w-fit">
          {STATUS_FILTERS.map(([key, label]) => {
            const n = key === "all"
              ? tenants.length
              : tenants.filter((t) => tenantStatus(t) === key).length;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`px-2.5 py-1 ${
                  statusFilter === key
                    ? "bg-noc-accent/20 text-noc-accent font-semibold"
                    : "text-noc-muted hover:text-noc-text"
                }`}
              >
                {label}{n ? ` (${n})` : ""}
              </button>
            );
          })}
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome, slug ou domínio…"
          className="h-8 px-2.5 rounded-lg bg-noc-bg border border-noc-border
                     text-xs text-noc-text sm:w-64"
        />
      </div>

      {/* tabela */}
      {loading ? (
        <div className="text-sm font-mono text-[12px] text-noc-faint">
          Carregando empresas…
        </div>
      ) : tenants.length === 0 ? (
        <div className="text-sm font-mono text-[12px] text-noc-faint
                        glass-card-pad">
          Nenhuma empresa cadastrada. Crie a primeira.
        </div>
      ) : visible.length === 0 ? (
        <div className="text-sm font-mono text-[12px] text-noc-faint
                        glass-card-pad">
          Nenhuma empresa neste filtro
          {query.trim() ? ` para "${query.trim()}"` : ""}.
        </div>
      ) : (
        <div className="glass-card-pad overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-noc-muted text-xs
                             border-b border-noc-border">
                <th className="py-2 pr-3">Empresa</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Domínio</th>
                <th className="py-2 pr-3">Cor</th>
                <th className="py-2 pr-3 text-right">Devices</th>
                <th className="py-2 pr-3 text-right">Usuários</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.id}
                    className="border-b border-noc-border/50">
                  <td className="py-2 pr-3">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-[11px] text-noc-muted
                                    font-mono">
                      {t.slug}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <StatusBadge status={tenantStatus(t)} />
                  </td>
                  <td className="py-2 pr-3 text-noc-muted">
                    {t.domain || "—"}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className="inline-block w-4 h-4 rounded
                                 border border-noc-border align-middle"
                      style={{ background: t.primary_color }}
                    />
                    <span className="ml-1.5 text-[11px]
                                     text-noc-muted font-mono">
                      {t.primary_color}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {t.device_count}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {t.user_count}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => setDetailId(t.id)}
                      className="text-xs text-noc-accent border
                                 border-noc-accent/40 rounded
                                 px-2 py-1"
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reload(); }}
        />
      )}
    </div>
  );
}

/* ──── Detalhe da empresa ──── */
function TenantDetail({ tenantId, tenants, onBack, onNavigate }) {
  const tenant = tenants.find((t) => t.id === tenantId);
  // SAAS-COMPANIES-P2-MAP-TAB — abas (padrão pages/FleetExplorer.jsx).
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({
    name: tenant?.name || "",
    primary_color: tenant?.primary_color || "#2BD9F0",
    domain: tenant?.domain || "",
    logo_url: tenant?.logo_url || "",
  });
  const [newUser, setNewUser] = useState({
    user_id: "", display_name: "", role: "viewer",
  });

  const loadUsers = useCallback(async () => {
    try {
      const data = await listTenantUsers(tenantId);
      setUsers(data.users || []);
    } catch (e) {
      setErr(e?.message || "Falha ao carregar usuários.");
    }
  }, [tenantId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  async function saveTenant() {
    setErr(null);
    try {
      await updateTenant(tenantId, form);
      setMsg("Empresa atualizada.");
    } catch (e) {
      setErr(e?.message || "Falha ao salvar.");
    }
  }

  async function addUser() {
    setErr(null);
    if (!newUser.user_id.trim()) {
      setErr("Informe o user_id.");
      return;
    }
    try {
      await addTenantUser({ ...newUser, tenant_id: tenantId });
      setNewUser({ user_id: "", display_name: "", role: "viewer" });
      setMsg("Usuário vinculado.");
      loadUsers();
    } catch (e) {
      setErr(e?.message || "Falha ao vincular usuário.");
    }
  }

  async function changeRole(userPk, role) {
    setErr(null);
    try {
      await updateUserRole(userPk, role);
      setMsg("Papel atualizado.");
      loadUsers();
    } catch (e) {
      setErr(e?.message || "Falha ao alterar papel.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-sm text-noc-muted border border-noc-border
                     rounded-lg px-3 py-1.5"
        >
          ← Empresas
        </button>
        <h1 className="text-2xl font-bold tracking-tight flex-1">
          {tenant?.name || "Empresa"}
        </h1>
        {tenant && <StatusBadge status={tenantStatus(tenant)} />}
      </div>

      {/* SAAS-COMPANIES-P2-MAP-TAB — abas do detalhe */}
      <nav className="flex gap-1 border-b border-noc-border/40 text-sm">
        {[
          ["overview", "Visão geral"],
          ["devices", "Dispositivos"],
          ["map", "🗺 Mapa"],
          ["inventory", "Estoque"],
          ["installs", "Instalações"],
          ["whitelabel", "White-label"],
          ["users", "Usuários"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setActiveTab(k)}
            className={`px-3 py-2 border-b-2 -mb-px ${
              activeTab === k
                ? "border-noc-ok/30 text-noc-ok font-semibold"
                : "border-transparent text-noc-muted hover:text-noc-text"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {err && (
        <div className="text-sm text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded-lg p-3">
          {err}
        </div>
      )}
      {msg && (
        <div className="text-sm text-noc-ok bg-noc-ok/10
                        border border-noc-ok/30 rounded-lg p-3">
          {msg}
        </div>
      )}

      {/* Visão geral — resumo leve dos counts P1 já disponíveis */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Status" value={STATUS_META[tenantStatus(tenant)]?.label || "—"} />
          <StatCard label="Dispositivos" value={tenant?.device_count ?? "—"} />
          <StatCard label="Usuários" value={tenant?.user_count ?? "—"} />
          <StatCard label="Domínio" value={tenant?.domain || "—"} />
        </div>
      )}

      {/* Dispositivos — tabela tenant-scoped (mesma fonte do mapa) */}
      {activeTab === "devices" && (
        <TenantDevicesTab tenantId={tenantId} onNavigate={onNavigate} />
      )}

      {/* Mapa — devices da empresa (backend ?tenant_id=) + centro localStorage */}
      {activeTab === "map" && (
        <DeviceMapPanel tenantId={tenantId} onNavigate={onNavigate} />
      )}

      {/* Estoque — device_inventory tenant-scoped (read-only) */}
      {activeTab === "inventory" && (
        <TenantInventoryTab tenantId={tenantId} />
      )}

      {/* Instalações — installation_sessions tenant-scoped (read-only) */}
      {activeTab === "installs" && (
        <TenantInstallationsTab tenantId={tenantId} onNavigate={onNavigate} />
      )}

      {/* white-label */}
      {activeTab === "whitelabel" && (
      <div className="glass-card-pad space-y-3">
        <div className="text-xs font-semibold uppercase
                        tracking-wide text-noc-muted">
          White-label
        </div>
        <Field label="Nome">
          <input
            className="installer-input"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Domínio">
          <input
            className="installer-input"
            placeholder="noc.suaempresa.com.br"
            value={form.domain}
            onChange={(e) =>
              setForm({ ...form, domain: e.target.value })}
          />
        </Field>
        <Field label="Logo (URL)">
          <input
            className="installer-input"
            placeholder="https://cdn/logo.png"
            value={form.logo_url}
            onChange={(e) =>
              setForm({ ...form, logo_url: e.target.value })}
          />
        </Field>
        <Field label="Cor primária">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) =>
                setForm({ ...form, primary_color: e.target.value })}
              className="h-9 w-14 rounded border border-noc-border
                         bg-transparent"
            />
            <span className="text-xs font-mono text-noc-muted">
              {form.primary_color}
            </span>
          </div>
        </Field>
        <button
          onClick={saveTenant}
          className="h-9 px-3 rounded-lg text-sm font-semibold
                     bg-noc-accent/15 text-noc-accent border
                     border-noc-accent/40"
        >
          Salvar
        </button>
      </div>
      )}

      {/* usuários */}
      {activeTab === "users" && (
      <div className="glass-card-pad space-y-3">
        <div className="text-xs font-semibold uppercase
                        tracking-wide text-noc-muted">
          Usuários e permissões
        </div>
        {users.length === 0 ? (
          <div className="text-sm font-mono text-[12px] text-noc-faint">
            Nenhum usuário vinculado.
          </div>
        ) : (
          <div className="space-y-1.5">
            {users.map((u) => (
              <div key={u.id}
                   className="flex items-center gap-2 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {u.display_name || u.user_id}
                  </div>
                  <div className="text-[11px] text-noc-muted
                                  font-mono">
                    {u.user_id}
                  </div>
                </div>
                <select
                  value={u.role}
                  onChange={(e) =>
                    changeRole(u.id, e.target.value)}
                  className="installer-input !w-auto text-xs"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* novo usuário */}
        <div className="border-t border-noc-border pt-3 space-y-2">
          <div className="text-[11px] text-noc-muted">
            Vincular usuário
          </div>
          <input
            className="installer-input"
            placeholder="user_id (ex.: lucas)"
            value={newUser.user_id}
            onChange={(e) =>
              setNewUser({ ...newUser, user_id: e.target.value })}
          />
          <input
            className="installer-input"
            placeholder="Nome de exibição"
            value={newUser.display_name}
            onChange={(e) => setNewUser({
              ...newUser, display_name: e.target.value,
            })}
          />
          <select
            className="installer-input"
            value={newUser.role}
            onChange={(e) =>
              setNewUser({ ...newUser, role: e.target.value })}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            onClick={addUser}
            className="h-9 px-3 rounded-lg text-sm font-semibold
                       border border-noc-border text-noc-text"
          >
            Vincular
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

/* ──── Modal criar empresa ──── */
function CreateTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", domain: "", primary_color: "#2BD9F0",
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!form.name.trim()) {
      setErr("Informe o nome da empresa.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await createTenant(form);
      onCreated();
    } catch (e) {
      setErr(e?.message || "Falha ao criar empresa.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center
                    justify-center p-4">
      <div className="bg-noc-panel border border-noc-border
                      rounded-xl p-4 w-full max-w-md space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold flex-1">
            Nova empresa
          </h2>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-noc-muted border
                       border-noc-border"
          >
            ✕
          </button>
        </div>

        {err && (
          <div className="text-sm text-noc-err bg-noc-err/10 border
                          border-noc-err/30 rounded-lg p-2">
            {err}
          </div>
        )}

        <Field label="Nome">
          <input
            className="installer-input"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Domínio (opcional)">
          <input
            className="installer-input"
            placeholder="noc.suaempresa.com.br"
            value={form.domain}
            onChange={(e) =>
              setForm({ ...form, domain: e.target.value })}
          />
        </Field>
        <Field label="Cor primária">
          <input
            type="color"
            value={form.primary_color}
            onChange={(e) =>
              setForm({ ...form, primary_color: e.target.value })}
            className="h-9 w-14 rounded border border-noc-border
                       bg-transparent"
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 h-9 rounded-lg text-sm border
                       border-noc-border text-noc-muted"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 h-9 rounded-lg text-sm font-semibold
                       bg-noc-accent/15 text-noc-accent border
                       border-noc-accent/40 disabled:opacity-40"
          >
            {busy ? "Criando…" : "Criar empresa"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──── Auxiliares ──── */
function StatCard({ label, value }) {
  return (
    <div className="glass-card-pad">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-noc-muted">{label}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-noc-muted">{label}</span>
      {children}
    </label>
  );
}
